import { useEffect, useRef, useState } from "react";
import {
  Modal,
  Row,
  Col,
  Form,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Tag,
  Button,
  message,
  Divider,
  Space,
  Alert,
  Tooltip,
  Empty,
  Popconfirm,
  Typography,
  Descriptions,
  Card,
} from "antd";
import {
  SaveOutlined,
  SearchOutlined,
  PlusOutlined,
  PlayCircleOutlined,
  SyncOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
  ClearOutlined,
  CopyOutlined,
} from "@ant-design/icons";
import {
  obtenerOcrEntrega,
  actualizarOcrEntrega,
  crearActaManual,
  listarProductosDeActa,
  crearProductoEnActa,
  procesarUnoOcr,
  vaciarProductosDeActa,
  vaciarEncabezadoDeActa,
  vaciarTodaLaActa,
  urlVerSoporte,
  buscarParticipantePorCedula,
  buscarOrganizacionPorNit,
  buscarProductos,
  copiarProductosOcrA,
} from "../../services/ocrActas/OcrEntregasService";
import { acortarNombreArchivo } from "./ocrEntregasUtils";
import { CompararOcrBd, BloqueResuelto } from "./ocrActasBloques";
import ProductoItemEditable from "./ProductoItemEditable";
import { BANDERAS_ACTA } from "./ocrEntregasBanderas";

const DEBOUNCE_MS = 400;

const ESTADOS_VALIDACION = [
  { value: "pendiente", label: "Pendiente" },
  { value: "validado", label: "Validado" },
  { value: "rechazado", label: "Rechazado" },
];

// Formulario compacto para agregar manualmente un producto que el OCR no
// detecto (o que la acta nunca tuvo, si es manual).
const AgregarProductoForm = ({ oidActa, oidProyecto, onAgregado, onCancelar }) => {
  const [form] = Form.useForm();
  const [opcionesProducto, setOpcionesProducto] = useState([]);
  const [buscandoProducto, setBuscandoProducto] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const debounceRef = useRef(null);

  const oidProducto = Form.useWatch("oid_producto", form);
  const [disponibleSeleccionado, setDisponibleSeleccionado] = useState(null);

  const handleBuscarProductos = (texto) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!texto || texto.trim().length < 2) {
      setOpcionesProducto([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setBuscandoProducto(true);
      try {
        const respuesta = await buscarProductos(oidProyecto, texto.trim());
        setOpcionesProducto(respuesta.data || []);
      } catch {
        setOpcionesProducto([]);
      } finally {
        setBuscandoProducto(false);
      }
    }, DEBOUNCE_MS);
  };

  const handleSeleccionarProducto = (oidSeleccionado) => {
    const producto = opcionesProducto.find((p) => p.oid === oidSeleccionado);
    if (!producto) return;
    form.setFieldsValue({ oid_producto: producto.oid, nombre_producto_bd: producto.nombre_producto });
    setDisponibleSeleccionado(producto.disponible);
  };

  const handleAgregar = async () => {
    try {
      const valores = await form.validateFields();
      setGuardando(true);
      await crearProductoEnActa(oidActa, valores);
      message.success("Producto agregado a la acta");
      form.resetFields();
      onAgregado?.();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.error || "Error al agregar el producto");
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Form form={form} layout="vertical">
      <Form.Item name="oid_producto" hidden>
        <InputNumber />
      </Form.Item>

      <Form.Item label="Buscar producto en la base de datos">
        {oidProducto ? (
          <Space>
            <BloqueResuelto oid={oidProducto} texto={form.getFieldValue("nombre_producto_bd")} />
            <Button
              size="small"
              onClick={() => {
                form.setFieldsValue({ oid_producto: null, nombre_producto_bd: null });
                setDisponibleSeleccionado(null);
              }}
            >
              Quitar
            </Button>
          </Space>
        ) : (
          <Select
            showSearch
            style={{ width: "100%" }}
            placeholder="Escriba para buscar (mín. 2 letras)"
            filterOption={false}
            onSearch={handleBuscarProductos}
            onSelect={handleSeleccionarProducto}
            loading={buscandoProducto}
            notFoundContent={buscandoProducto ? "Buscando..." : "Sin resultados"}
            options={opcionesProducto.map((p) => ({
              value: p.oid,
              label: `${p.nombre_producto} — disponible: ${p.disponible}`,
            }))}
          />
        )}
      </Form.Item>

      {!oidProducto && (
        <Form.Item name="nombre_producto_bd" label="O escriba el nombre manualmente">
          <Input placeholder="Nombre del producto" />
        </Form.Item>
      )}

      {oidProducto && disponibleSeleccionado !== null && (
        <Alert
          type={disponibleSeleccionado > 0 ? "info" : "warning"}
          showIcon
          style={{ marginBottom: 12 }}
          message={`Disponible actualmente: ${disponibleSeleccionado}`}
        />
      )}

      <Row gutter={12}>
        <Col xs={24} sm={8}>
          <Form.Item label="Cantidad" name="cantidad" rules={[{ required: true, message: "Requerido" }]}>
            <InputNumber style={{ width: "100%" }} min={0} />
          </Form.Item>
        </Col>
      </Row>
      <Typography.Text type="secondary" style={{ display: "block", marginBottom: 12 }}>
        El N° PO, valor unitario, valor total y saldo se calculan automáticamente desde la base de productos del
        proyecto al guardar.
      </Typography.Text>

      <Space>
        <Button type="primary" icon={<SaveOutlined />} loading={guardando} onClick={handleAgregar}>
          Agregar producto
        </Button>
        <Button onClick={onCancelar}>Cancelar</Button>
      </Space>
    </Form>
  );
};

// Permite tomar los productos ya validados de esta acta y aplicarlos tal
// cual (mismos productos y cantidades) a otras actas que el usuario ya
// verificó que recibieron exactamente lo mismo. Se identifica el destino por
// su # (oid) y no por cédula/NIT, porque un mismo participante u
// organización puede tener varias actas en el mismo proyecto y la cédula/NIT
// dejaría ambiguo a cuál de ellas aplicar la copia. En cada acta destino se
// reemplazan por completo los productos que tuviera, para no dejar mezclas
// ni duplicados si esa acta ya había sido procesada distinto.
const CopiarProductosModal = ({ open, oidActa, totalProductos, onClose, onCopiado }) => {
  const [textoOids, setTextoOids] = useState("");
  const [copiando, setCopiando] = useState(false);
  const [resultados, setResultados] = useState(null);

  const handleCerrar = () => {
    setTextoOids("");
    setResultados(null);
    onClose?.();
  };

  const handleCopiar = async () => {
    const oidsDestino = textoOids
      .split(/[\n,;]+/)
      .map((s) => parseInt(s.trim(), 10))
      .filter((n) => !isNaN(n) && n > 0);

    if (oidsDestino.length === 0) {
      message.warning("Escriba al menos un # de registro");
      return;
    }

    setCopiando(true);
    setResultados(null);
    try {
      const respuesta = await copiarProductosOcrA(oidActa, oidsDestino);
      const data = respuesta.data || [];
      setResultados(data);
      const exitosos = data.filter((r) => r.ok).length;
      if (exitosos > 0) {
        message.success(`Productos copiados a ${exitosos} de ${data.length} acta(s)`);
        onCopiado?.();
      }
    } catch (error) {
      message.error(error.response?.data?.error || "Error al copiar los productos");
    } finally {
      setCopiando(false);
    }
  };

  return (
    <Modal
      title="Copiar productos a otras actas"
      open={open}
      onCancel={handleCerrar}
      footer={[
        <Button key="cerrar" onClick={handleCerrar}>
          Cerrar
        </Button>,
        <Button key="copiar" type="primary" icon={<CopyOutlined />} loading={copiando} onClick={handleCopiar}>
          Copiar
        </Button>,
      ]}
      destroyOnClose
    >
      <Alert
        type="warning"
        showIcon
        style={{ marginBottom: 16 }}
        message={`Se copiarán los ${totalProductos} producto(s) (con sus cantidades) a las actas indicadas.`}
        description="Si el acta destino ya tenía productos cargados, se borrarán primero para evitar duplicados. Revise cada acta después de copiar."
      />

      <Typography.Text strong>
        # de registro de las actas destino{" "}
        <Typography.Text type="secondary">(el # lo ves en la columna # de la tabla)</Typography.Text>
      </Typography.Text>
      <Input.TextArea
        rows={5}
        style={{ marginTop: 8, marginBottom: 16 }}
        placeholder={"123\n456\n789\n..."}
        value={textoOids}
        onChange={(e) => setTextoOids(e.target.value)}
        disabled={copiando}
      />

      {resultados && (
        <div>
          <Divider style={{ margin: "8px 0 12px" }}>Resultado</Divider>
          <Space direction="vertical" style={{ width: "100%" }} size={6}>
            {resultados.map((r) => (
              <div key={r.oid_destino}>
                <Tag color={r.ok ? "success" : "error"}>#{r.oid_destino}</Tag>
                {r.ok ? (
                  <Typography.Text type="secondary">
                    Copiado ({r.productos_copiados} producto(s)
                    {r.productos_reemplazados > 0 ? `, se reemplazaron ${r.productos_reemplazados} existentes` : ""})
                  </Typography.Text>
                ) : (
                  <Typography.Text type="danger">{r.motivo}</Typography.Text>
                )}
              </div>
            ))}
          </Space>
        </div>
      )}
    </Modal>
  );
};

// Modal tipo factura: encabezado de la acta (proyecto, ubicación, fecha,
// beneficiario) + tabla de productos aparte. Sirve tanto para editar una
// acta existente (oid presente) como para crear una completamente manual
// (oid null): en ese caso los productos solo se pueden agregar despues de
// guardar el encabezado por primera vez, porque necesitan un oid de acta.
const ActaFormModal = ({
  open,
  oid,
  oidProyecto,
  permissionUpdate,
  permissionDelete,
  permissionPrint,
  onClose,
  onGuardado,
  onCreado,
  colaCopia = {},
  todasLasActas = [],
  onAgregarACola,
  onQuitarDeCola,
  onLimpiarCola,
}) => {
  const modoCrear = !oid;
  const [headerForm] = Form.useForm();
  const [fila, setFila] = useState(null);
  const [items, setItems] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [guardandoHeader, setGuardandoHeader] = useState(false);
  const [procesando, setProcesando] = useState(false);
  const [proveedorOcr, setProveedorOcr] = useState("vision");
  const [mostrarAgregarProducto, setMostrarAgregarProducto] = useState(false);
  const [mostrarCopiarProductos, setMostrarCopiarProductos] = useState(false);
  const [oidReferenteParaCola, setOidReferenteParaCola] = useState(null);
  const [procesandoCola, setProcesandoCola] = useState(false);
  const [resultadosCola, setResultadosCola] = useState(null);

  const [tipoActaCrear, setTipoActaCrear] = useState("participante");
  const [cedulaBusqueda, setCedulaBusqueda] = useState("");
  const [nitBusqueda, setNitBusqueda] = useState("");
  const [buscandoBeneficiario, setBuscandoBeneficiario] = useState(false);
  const [beneficiarioNoEncontrado, setBeneficiarioNoEncontrado] = useState(false);
  const [pantallaCompleta, setPantallaCompleta] = useState(false);

  const oidParticipante = Form.useWatch("oid_participante", headerForm);
  const oidOrganizacion = Form.useWatch("oid_organizacion", headerForm);
  const nombreParticipanteBd = Form.useWatch("nombre_participante_bd", headerForm);
  const nombreOrganizacionBd = Form.useWatch("nombre_organizacion_bd", headerForm);

  const cargar = async () => {
    if (modoCrear) {
      headerForm.resetFields();
      setBeneficiarioNoEncontrado(false);
      return;
    }

    setCargando(true);
    setBeneficiarioNoEncontrado(false);
    setMostrarAgregarProducto(false);
    try {
      const datos = await obtenerOcrEntrega(oid);
      setFila(datos);
      setTipoActaCrear(datos.tipo_acta === "organizacion" ? "organizacion" : "participante");
      setCedulaBusqueda(datos.oid_participante ? "" : datos.cedula_participante_ocr || "");
      setNitBusqueda(datos.oid_organizacion ? "" : datos.nit_organizacion_ocr || "");
      headerForm.setFieldsValue({
        oid_participante: datos.oid_participante,
        nombre_participante_bd: datos.nombre_participante_bd,
        oid_organizacion: datos.oid_organizacion,
        nombre_organizacion_bd: datos.nombre_organizacion_bd,
        estado_validacion: datos.estado_validacion,
      });

      const respuestaItems = await listarProductosDeActa(oid);
      setItems(respuestaItems.data || []);
    } catch (error) {
      message.error(error.response?.data?.error || "Error al cargar el registro");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    setFila(null);
    setItems([]);
    setTipoActaCrear("participante");
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, oid]);

  const handleBuscarParticipante = async () => {
    if (!cedulaBusqueda.trim()) return;
    setBuscandoBeneficiario(true);
    try {
      const encontrado = await buscarParticipantePorCedula(cedulaBusqueda.trim());
      headerForm.setFieldsValue({
        oid_participante: encontrado.oid,
        nombre_participante_bd: encontrado.nombre_participante,
      });
      setBeneficiarioNoEncontrado(false);
      message.success("Participante encontrado en la base de datos");
    } catch (error) {
      setBeneficiarioNoEncontrado(true);
      message.warning(error.response?.data?.error || "No se encontró un participante con esa cédula");
    } finally {
      setBuscandoBeneficiario(false);
    }
  };

  const handleBuscarOrganizacion = async () => {
    if (!nitBusqueda.trim()) return;
    setBuscandoBeneficiario(true);
    try {
      const encontrada = await buscarOrganizacionPorNit(nitBusqueda.trim());
      headerForm.setFieldsValue({
        oid_organizacion: encontrada.oid,
        nombre_organizacion_bd: encontrada.organizacion,
      });
      setBeneficiarioNoEncontrado(false);
      message.success("Organización encontrada en la base de datos");
    } catch (error) {
      setBeneficiarioNoEncontrado(true);
      message.warning(error.response?.data?.error || "No se encontró una organización con ese NIT");
    } finally {
      setBuscandoBeneficiario(false);
    }
  };

  const handleQuitarBeneficiario = () => {
    if (esParticipante) {
      headerForm.setFieldsValue({ oid_participante: null });
      setCedulaBusqueda(fila?.cedula_participante_ocr || "");
    } else {
      headerForm.setFieldsValue({ oid_organizacion: null });
      setNitBusqueda(fila?.nit_organizacion_ocr || "");
    }
    setBeneficiarioNoEncontrado(false);
  };

  const handleGuardarHeader = async () => {
    if (!permissionUpdate) {
      message.warning("No tiene permisos para editar este registro");
      return;
    }
    try {
      const valores = await headerForm.validateFields();
      setGuardandoHeader(true);

      if (modoCrear) {
        const payload = {
          oid_proyecto: oidProyecto,
          tipo_acta: tipoActaCrear,
          fecha_archivo: valores.fecha_archivo ? valores.fecha_archivo.format("YYYY-MM-DD") : null,
          fecha_ocr: valores.fecha_ocr ? valores.fecha_ocr.format("YYYY-MM-DD") : null,
          oid_departamento: null,
          departamento_ocr: valores.departamento_ocr || null,
          oid_municipio: null,
          municipio_ocr: valores.municipio_ocr || null,
          cedula_participante_ocr: tipoActaCrear === "participante" ? cedulaBusqueda || null : null,
          oid_participante: valores.oid_participante ?? null,
          nombre_participante_bd: valores.nombre_participante_bd ?? null,
          nit_organizacion_ocr: tipoActaCrear === "organizacion" ? nitBusqueda || null : null,
          oid_organizacion: valores.oid_organizacion ?? null,
          nombre_organizacion_bd: valores.nombre_organizacion_bd ?? null,
        };
        const creada = await crearActaManual(payload);
        message.success("Acta creada correctamente. Ahora puede agregarle productos.");
        onGuardado?.();
        onCreado?.(creada.oid);
      } else {
        await actualizarOcrEntrega(oid, {
          tipo_acta: tipoActaCrear,
          oid_participante: valores.oid_participante ?? null,
          nombre_participante_bd: valores.nombre_participante_bd ?? null,
          oid_organizacion: valores.oid_organizacion ?? null,
          nombre_organizacion_bd: valores.nombre_organizacion_bd ?? null,
          estado_validacion: valores.estado_validacion,
        });
        message.success("Encabezado actualizado correctamente");
        onGuardado?.();
        await cargar();
      }
    } catch (error) {
      if (error?.errorFields) return;
      message.error(error.response?.data?.error || "Error al guardar el encabezado");
    } finally {
      setGuardandoHeader(false);
    }
  };

  const handleProcesar = async () => {
    setProcesando(true);
    try {
      await procesarUnoOcr(oid, proveedorOcr);
      message.success("Acta procesada correctamente");
      onGuardado?.();
      await cargar();
    } catch (error) {
      message.error(error.response?.data?.error || "Error al procesar el acta");
    } finally {
      setProcesando(false);
    }
  };

  const [vaciando, setVaciando] = useState(null);

  const handleVaciar = async (tipo) => {
    setVaciando(tipo);
    try {
      if (tipo === "productos") await vaciarProductosDeActa(oid);
      else if (tipo === "encabezado") await vaciarEncabezadoDeActa(oid);
      else await vaciarTodaLaActa(oid);
      message.success("Acta actualizada correctamente. El registro del archivo se conserva (ver histórico).");
      onGuardado?.();
      await cargar();
    } catch (error) {
      message.error(error.response?.data?.error || "Error al vaciar la acta");
    } finally {
      setVaciando(null);
    }
  };

  const handleProductoGuardado = (itemActualizado) => {
    setItems((prev) => prev.map((it) => (it.oid === itemActualizado.oid ? itemActualizado : it)));
    onGuardado?.();
  };

  const handleProductoEliminado = (oidEliminado) => {
    setItems((prev) => prev.filter((it) => it.oid !== oidEliminado));
    onGuardado?.();
  };

  const handleProductoAgregado = async () => {
    setMostrarAgregarProducto(false);
    const respuestaItems = await listarProductosDeActa(oid);
    setItems(respuestaItems.data || []);
    onGuardado?.();
  };

  // ── Cola de copia ──────────────────────────────────────────────────────────
  // oids que el usuario marcó para recibir los productos de ESTE acta (referente)
  const colaEsteReferente = colaCopia[oid] || [];

  // Si este acta está marcada como destino de algún referente, ¿cuál?
  const oidReferenteActual = Number(
    Object.entries(colaCopia).find(([, oids]) => oids.includes(oid))?.[0] ?? 0
  ) || null;

  // Referentes disponibles: actas del mismo proyecto con productos (excluye la actual)
  const referentesDisponibles = todasLasActas.filter(
    (a) => a.oid !== oid && a.total_productos > 0
  );

  const handleProcesarColaLocal = async () => {
    if (colaEsteReferente.length === 0) return;
    setProcesandoCola(true);
    setResultadosCola(null);
    try {
      const respuesta = await copiarProductosOcrA(oid, colaEsteReferente);
      const data = respuesta.data || [];
      setResultadosCola(data);
      const exitosos = data.filter((r) => r.ok).length;
      if (exitosos > 0) {
        message.success(`Cola procesada: ${exitosos} de ${colaEsteReferente.length} acta(s) actualizadas`);
        onLimpiarCola?.(oid);
        onGuardado?.();
      }
    } catch (error) {
      message.error(error.response?.data?.error || "Error al procesar la cola");
    } finally {
      setProcesandoCola(false);
    }
  };
  // ───────────────────────────────────────────────────────────────────────────

  const bloqueado = !permissionUpdate || cargando;
  const esParticipante = tipoActaCrear === "participante";
  const oidBeneficiarioResuelto = esParticipante ? oidParticipante : oidOrganizacion;
  const nombreOcrBeneficiario = esParticipante ? fila?.nombre_participante_ocr : fila?.nombre_organizacion_ocr;
  const idOcrBeneficiario = esParticipante ? fila?.cedula_participante_ocr : fila?.nit_organizacion_ocr;
  const puedeProcesar = !modoCrear && fila?.origen === "archivo";

  return (
    <Modal
      title={
        <Space>
          <span>
            {modoCrear
              ? "Crear acta manual"
              : fila
              ? `Validar acta #${oid}: ${fila.nombre_archivo ? acortarNombreArchivo(fila.nombre_archivo) : "(manual)"}`
              : "Validar acta"}
          </span>
          {!modoCrear && (
            <Tooltip title={pantallaCompleta ? "Salir de pantalla completa" : "Ver en pantalla completa"}>
              <Button
                size="small"
                type="text"
                icon={pantallaCompleta ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                onClick={() => setPantallaCompleta((v) => !v)}
              />
            </Tooltip>
          )}
        </Space>
      }
      open={open}
      onCancel={onClose}
      width={pantallaCompleta ? "96vw" : 1440}
      style={pantallaCompleta ? { top: 12 } : undefined}
      styles={{
        body: {
          padding: "20px 24px",
          ...(pantallaCompleta ? { height: "calc(100vh - 200px)", overflowY: "auto" } : {}),
        },
      }}
      footer={[
        <Button key="cerrar" onClick={onClose}>
          Cerrar
        </Button>,
      ]}
      destroyOnClose
    >
      <Row gutter={20}>
        <Col
          xs={24}
          md={puedeProcesar ? 13 : 24}
          style={{ maxHeight: pantallaCompleta ? "calc(100vh - 260px)" : 800, overflowY: "auto" }}
        >
          {!modoCrear && fila && (
            <Card
              size="small"
              style={{ marginBottom: 20, borderColor: "#e8e8e8", borderRadius: 8 }}
              styles={{ body: { padding: 20 } }}
            >
              <Descriptions column={2} size="small" colon={false}>
                <Descriptions.Item label="Tipo de acta">
                  <Tag color={esParticipante ? "blue" : "purple"}>{fila.tipo_acta}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Proveedor OCR usado">
                  {fila.proveedor_ocr === "document_ai" ? (
                    <Tag color="purple">Document AI</Tag>
                  ) : (
                    <Tag color="blue">Vision</Tag>
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Origen">
                  <Tag color="cyan">{fila.origen === "manual" ? "Acta manual" : "Desde archivo"}</Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Proyecto">
                  {fila.proyecto_codigo ? `${fila.proyecto_codigo} - ${fila.proyecto_nombre}` : "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Carpeta">{fila.carpeta_mes || "-"}</Descriptions.Item>
                <Descriptions.Item label="Departamento">{fila.departamento_ocr || "-"}</Descriptions.Item>
                <Descriptions.Item label="Municipio">{fila.municipio_ocr || "-"}</Descriptions.Item>
                <Descriptions.Item label="Fecha archivo">{fila.fecha_archivo?.split?.("T")[0] || "-"}</Descriptions.Item>
                <Descriptions.Item label="Fecha en el acta (OCR)">{fila.fecha_ocr || "-"}</Descriptions.Item>
                {fila.ruta_acta && (
                  <Descriptions.Item label="Ruta del archivo" span={2}>
                    <Typography.Text type="secondary" style={{ fontSize: 12, wordBreak: "break-all" }}>
                      {fila.ruta_acta}
                    </Typography.Text>
                  </Descriptions.Item>
                )}
              </Descriptions>

              {BANDERAS_ACTA.some((b) => fila[b.key]) && (
                <Space size={[8, 8]} wrap style={{ marginTop: 12 }}>
                  {BANDERAS_ACTA.filter((b) => fila[b.key]).map((b) => (
                    <Tooltip key={b.key} title={b.descripcion}>
                      <Tag color="error">{b.label}</Tag>
                    </Tooltip>
                  ))}
                </Space>
              )}

              <Space style={{ marginTop: 16 }} wrap>
                {puedeProcesar && permissionUpdate && (
                  <Select
                    value={proveedorOcr}
                    onChange={setProveedorOcr}
                    size="small"
                    style={{ width: 160 }}
                    options={[
                      { value: "vision", label: "Google Vision" },
                      { value: "document_ai", label: "Document AI" },
                    ]}
                  />
                )}
                {puedeProcesar && permissionUpdate && (
                  <Popconfirm
                    title={
                      fila.estado_procesamiento === "procesado"
                        ? "¿Reprocesar esta acta? Se vuelve a leer el PDF y se reemplazan los productos detectados."
                        : "¿Procesar esta acta? Esto consume cuota de Google Vision."
                    }
                    onConfirm={handleProcesar}
                  >
                    <Button
                      size="small"
                      type="primary"
                      icon={fila.estado_procesamiento === "procesado" ? <SyncOutlined /> : <PlayCircleOutlined />}
                      loading={procesando}
                    >
                      {fila.estado_procesamiento === "procesado" ? "Reprocesar" : "Procesar"}
                    </Button>
                  </Popconfirm>
                )}
                {permissionDelete && (
                  <>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      Vaciar (queda en histórico, no borra el pre-registro):
                    </Typography.Text>
                    <Popconfirm
                      title="¿Vaciar solo los productos de esta acta?"
                      onConfirm={() => handleVaciar("productos")}
                    >
                      <Button size="small" icon={<ClearOutlined />} loading={vaciando === "productos"}>
                        Vaciar productos
                      </Button>
                    </Popconfirm>
                    <Popconfirm
                      title="¿Vaciar solo el encabezado (beneficiario, ubicación, tipo de acta)?"
                      onConfirm={() => handleVaciar("encabezado")}
                    >
                      <Button size="small" icon={<ClearOutlined />} loading={vaciando === "encabezado"}>
                        Vaciar encabezado
                      </Button>
                    </Popconfirm>
                    <Popconfirm title="¿Vaciar todo (encabezado y productos)?" onConfirm={() => handleVaciar("todo")}>
                      <Button size="small" danger type="text" icon={<ClearOutlined />} loading={vaciando === "todo"}>
                        Vaciar todo
                      </Button>
                    </Popconfirm>
                  </>
                )}
              </Space>
            </Card>
          )}

          <Divider style={{ margin: "8px 0 16px" }}>Datos de la acta</Divider>

          {(modoCrear || permissionUpdate) && (
            <CampoTipoActa value={tipoActaCrear} onChange={setTipoActaCrear} disabled={bloqueado} />
          )}

          <Card size="small" style={{ marginBottom: 16 }} styles={{ body: { padding: "16px 16px 4px" } }}>
            <Form form={headerForm} layout="vertical" disabled={bloqueado}>
              <Form.Item name="oid_participante" hidden>
                <InputNumber />
              </Form.Item>
              <Form.Item name="oid_organizacion" hidden>
                <InputNumber />
              </Form.Item>

              {modoCrear && (
                <Row gutter={[12, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Fecha del archivo" name="fecha_archivo">
                      <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Fecha en el acta" name="fecha_ocr">
                      <DatePicker style={{ width: "100%" }} format="DD/MM/YYYY" />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {modoCrear && (
                <Row gutter={[12, 0]}>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Departamento" name="departamento_ocr">
                      <Input placeholder="Nombre del departamento" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={12}>
                    <Form.Item label="Municipio" name="municipio_ocr">
                      <Input placeholder="Nombre del municipio" />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              <Divider style={{ margin: "8px 0 16px" }}>{esParticipante ? "Participante" : "Organización"}</Divider>

              <Card
                size="small"
                style={{ marginBottom: 20, borderColor: "#e8e8e8", borderRadius: 8 }}
                styles={{ body: { padding: 20 } }}
              >
                <CompararOcrBd
                  etiquetaOcr={`Identificación${modoCrear ? "" : " OCR"} (${esParticipante ? "cédula" : "NIT"})`}
                  valorOcr={modoCrear ? null : idOcrBeneficiario}
                >
                  {oidBeneficiarioResuelto ? (
                    <BloqueResuelto
                      oid={oidBeneficiarioResuelto}
                      identificacion={
                        esParticipante ? cedulaBusqueda || idOcrBeneficiario : nitBusqueda || idOcrBeneficiario
                      }
                      texto={esParticipante ? nombreParticipanteBd : nombreOrganizacionBd}
                    />
                  ) : (
                    <Space.Compact style={{ width: "100%" }}>
                      <Input
                        placeholder={esParticipante ? "Cédula del participante" : "NIT de la organización"}
                        value={esParticipante ? cedulaBusqueda : nitBusqueda}
                        onChange={(e) =>
                          esParticipante ? setCedulaBusqueda(e.target.value) : setNitBusqueda(e.target.value)
                        }
                        onPressEnter={esParticipante ? handleBuscarParticipante : handleBuscarOrganizacion}
                        disabled={bloqueado}
                      />
                      <Button
                        icon={<SearchOutlined />}
                        onClick={esParticipante ? handleBuscarParticipante : handleBuscarOrganizacion}
                        loading={buscandoBeneficiario}
                        disabled={bloqueado}
                      >
                        Buscar
                      </Button>
                    </Space.Compact>
                  )}
                </CompararOcrBd>

                {beneficiarioNoEncontrado && (
                  <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="No se encontró en la base de datos. Escriba el nombre manualmente abajo; la fila quedará marcada para revisión."
                  />
                )}

                <CompararOcrBd
                  etiquetaOcr="Nombre"
                  valorOcr={modoCrear ? null : nombreOcrBeneficiario}
                  noCoincide={!modoCrear && fila?.bandera_nombre_beneficiario_no_coincide}
                >
                  <Form.Item
                    name={esParticipante ? "nombre_participante_bd" : "nombre_organizacion_bd"}
                    style={{ marginBottom: 0 }}
                  >
                    <Input placeholder="Nombre del beneficiario" />
                  </Form.Item>
                </CompararOcrBd>

                {oidBeneficiarioResuelto && permissionUpdate && (
                  <Button size="small" onClick={handleQuitarBeneficiario}>
                    Quitar y buscar de nuevo
                  </Button>
                )}
              </Card>

            {!modoCrear && (
              <Form.Item label="Estado de validación" name="estado_validacion" rules={[{ required: true }]}>
                <Select options={ESTADOS_VALIDACION} style={{ maxWidth: 260 }} />
              </Form.Item>
            )}

              {permissionUpdate && (
                <Form.Item>
                  <Button type="primary" icon={<SaveOutlined />} loading={guardandoHeader} onClick={handleGuardarHeader}>
                    {modoCrear ? "Crear acta" : "Guardar encabezado"}
                  </Button>
                </Form.Item>
              )}
            </Form>
          </Card>

          {!modoCrear && (
            <>
              <Divider style={{ margin: "24px 0 16px" }}>
                Productos entregados {items.length > 0 && `(${items.length})`}
              </Divider>

              {/* ── Si esta acta ES un referente con cola activa ── */}
              {colaEsteReferente.length > 0 && (
                <Alert
                  type="success"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={
                    <Space>
                      <span>
                        Cola activa: <b>{colaEsteReferente.length}</b> acta(s) recibirán estos{" "}
                        <b>{items.length}</b> producto(s)
                      </span>
                      <Popconfirm
                        title={`¿Procesar la cola? Se copiarán ${items.length} producto(s) a ${colaEsteReferente.length} acta(s) y quedarán validadas.`}
                        onConfirm={handleProcesarColaLocal}
                        okText="Procesar"
                        cancelText="Cancelar"
                      >
                        <Button size="small" type="primary" loading={procesandoCola}>
                          Procesar cola
                        </Button>
                      </Popconfirm>
                    </Space>
                  }
                />
              )}
              {resultadosCola && (
                <div style={{ marginBottom: 12 }}>
                  <Space direction="vertical" style={{ width: "100%" }} size={4}>
                    {resultadosCola.map((r) => (
                      <div key={r.oid_destino}>
                        <Tag color={r.ok ? "success" : "error"}>#{r.oid_destino}</Tag>
                        {r.ok ? (
                          <Typography.Text type="secondary">
                            Copiado ({r.productos_copiados} producto(s)
                            {r.productos_reemplazados > 0
                              ? `, reemplazó ${r.productos_reemplazados} existentes`
                              : ""})
                          </Typography.Text>
                        ) : (
                          <Typography.Text type="danger">{r.motivo}</Typography.Text>
                        )}
                      </div>
                    ))}
                  </Space>
                </div>
              )}

              {/* ── Si esta acta está marcada como destino de algún referente ── */}
              {oidReferenteActual && (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 12 }}
                  message={
                    <Space>
                      <span>
                        En cola del referente <b>#{oidReferenteActual}</b> — recibirá sus productos al
                        procesar la cola
                      </span>
                      <Button
                        size="small"
                        onClick={() => onQuitarDeCola?.(oidReferenteActual, oid)}
                      >
                        Quitar de la cola
                      </Button>
                    </Space>
                  }
                />
              )}

              {/* ── Agregar esta acta a la cola de un referente ── */}
              {!oidReferenteActual && referentesDisponibles.length > 0 && (
                <Space style={{ marginBottom: 12 }} wrap>
                  <Select
                    showSearch
                    placeholder="Buscar referente por nombre, # o cantidad..."
                    style={{ minWidth: 360 }}
                    value={oidReferenteParaCola}
                    onChange={setOidReferenteParaCola}
                    allowClear
                    filterOption={(input, option) =>
                      option.label.toLowerCase().includes(input.toLowerCase())
                    }
                    options={referentesDisponibles.map((a) => {
                      const nombre =
                        a.nombre_participante_bd ||
                        a.nombre_organizacion_bd ||
                        a.nombre_participante_ocr ||
                        a.nombre_organizacion_ocr ||
                        "Sin nombre";
                      return {
                        value: a.oid,
                        label: `#${a.oid} — ${nombre} (${a.total_productos} productos)`,
                      };
                    })}
                  />
                  <Button
                    disabled={!oidReferenteParaCola}
                    onClick={() => {
                      onAgregarACola?.(oidReferenteParaCola, oid);
                      message.success(`Acta #${oid} agregada a la cola del referente #${oidReferenteParaCola}`);
                      setOidReferenteParaCola(null);
                    }}
                  >
                    Agregar a cola
                  </Button>
                </Space>
              )}

              {items.length > 0 && permissionUpdate && (
                <Button
                  icon={<CopyOutlined />}
                  style={{ marginBottom: 16 }}
                  onClick={() => setMostrarCopiarProductos(true)}
                >
                  Copiar estos productos a otras actas
                </Button>
              )}

              {items.length === 0 && !cargando ? (
                <Empty description="Sin productos en esta acta" style={{ margin: "24px 0" }} />
              ) : (
                items.map((item, idx) => (
                  <ProductoItemEditable
                    key={item.oid}
                    item={item}
                    oidProyecto={fila?.oid_proyecto}
                    titulo={`Producto ${idx + 1} de ${items.length}`}
                    permissionUpdate={permissionUpdate}
                    puedeEliminar={items.length > 1}
                    onGuardado={handleProductoGuardado}
                    onEliminado={handleProductoEliminado}
                  />
                ))
              )}

              {permissionUpdate && (
                <>
                  {mostrarAgregarProducto ? (
                    <Card
                      size="small"
                      title="Agregar producto"
                      style={{ marginBottom: 20, borderColor: "#e8e8e8", borderRadius: 8 }}
                      styles={{ body: { padding: 20 } }}
                    >
                      <AgregarProductoForm
                        oidActa={oid}
                        oidProyecto={fila?.oid_proyecto}
                        onAgregado={handleProductoAgregado}
                        onCancelar={() => setMostrarAgregarProducto(false)}
                      />
                    </Card>
                  ) : (
                    <Button
                      type="dashed"
                      icon={<PlusOutlined />}
                      block
                      onClick={() => setMostrarAgregarProducto(true)}
                      style={{ marginBottom: 16 }}
                    >
                      Agregar producto
                    </Button>
                  )}
                </>
              )}
            </>
          )}

          {modoCrear && (
            <Alert
              type="info"
              showIcon
              style={{ marginTop: 16 }}
              message="Guarde el encabezado primero. Después podrá agregarle los productos a esta acta."
            />
          )}
        </Col>

        {puedeProcesar && (
          <Col xs={24} md={11}>
            {permissionPrint ? (
              <iframe
                title="Soporte PDF"
                src={urlVerSoporte(oid)}
                style={{
                  width: "100%",
                  height: pantallaCompleta ? "calc(100vh - 220px)" : 760,
                  border: "1px solid #d9d9d9",
                  borderRadius: 4,
                }}
              />
            ) : (
              <div style={{ textAlign: "center", color: "#888", paddingTop: 60 }}>
                No tiene permisos para ver el soporte
              </div>
            )}
          </Col>
        )}
      </Row>

      <CopiarProductosModal
        open={mostrarCopiarProductos}
        oidActa={oid}
        totalProductos={items.length}
        onClose={() => setMostrarCopiarProductos(false)}
        onCopiado={onGuardado}
      />
    </Modal>
  );
};

const CampoTipoActa = ({ value, onChange, disabled }) => (
  <Form.Item label="Tipo de acta" required style={{ maxWidth: 260 }}>
    <Select
      value={value}
      onChange={onChange}
      disabled={disabled}
      options={[
        { value: "participante", label: "Participante" },
        { value: "organizacion", label: "Organización" },
      ]}
    />
  </Form.Item>
);

export default ActaFormModal;
